import { useEffect, useState } from "react";
import { formatMoney } from "@mavatech/shared";
import { cn } from "@/lib/cn";
import { trpc } from "@/lib/trpc";

const KEY = "mavatech_article_supplier";

/** Gewählter Händler-Filter der Artikelsuche — wird gemerkt (gilt für Positionen, Artikelsuche und Artikelliste). */
export function useSupplierFilter(): [string, (v: string) => void] {
  const [value, setValue] = useState<string>(() => {
    try {
      return localStorage.getItem(KEY) ?? "";
    } catch {
      return "";
    }
  });
  const set = (v: string) => {
    setValue(v);
    try {
      if (v) localStorage.setItem(KEY, v);
      else localStorage.removeItem(KEY);
      window.dispatchEvent(new Event("mavatech-supplier-filter"));
    } catch {
      /* ohne Speicher: gilt nur für diese Ansicht */
    }
  };
  // mehrere geöffnete Suchfelder halten denselben Stand
  useEffect(() => {
    const sync = () => {
      try {
        setValue(localStorage.getItem(KEY) ?? "");
      } catch {
        /* egal */
      }
    };
    window.addEventListener("mavatech-supplier-filter", sync);
    return () => window.removeEventListener("mavatech-supplier-filter", sync);
  }, []);
  return [value, set];
}

/** Eingabe verzögern, damit nicht jeder Tastendruck eine Suche über 1,7 Mio. Artikel auslöst. */
export function useDebounced<T>(value: T, ms = 250): T {
  const [v, setV] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setV(value), ms);
    return () => clearTimeout(t);
  }, [value, ms]);
  return v;
}

/** Händler-Auswahl als Schaltflächen: „Alle · Impex · Steiner · …". */
export function SupplierChips({
  value,
  onChange,
  className,
}: {
  value: string;
  onChange: (id: string) => void;
  className?: string;
}) {
  const suppliers = trpc.supplier.withArticles.useQuery(undefined, {
    staleTime: 5 * 60_000,
  });
  // nur Händler, von denen schon Artikel im System sind
  // feste Reihenfolge: Impex, Steiner, Holter, ÖAG — weitere Händler danach alphabetisch
  const ORDER = ["impex", "steiner", "holter", "öag"];
  const rank = (name: string) => {
    const i = ORDER.indexOf(name.trim().toLowerCase());
    return i === -1 ? ORDER.length : i;
  };
  const list = [...(suppliers.data ?? [])].sort(
    (a, b) => rank(a.name) - rank(b.name) || a.name.localeCompare(b.name, "de"),
  );
  if (list.length < 2) return null;
  const chip = (id: string, label: string) => (
    <button
      key={id || "all"}
      type="button"
      // mousedown statt click: das Eingabefeld behält den Fokus, die Trefferliste bleibt offen
      onMouseDown={(e) => {
        e.preventDefault();
        onChange(id);
      }}
      className={cn(
        "px-2 py-0.5 rounded-full border text-2xs transition-colors",
        value === id
          ? "bg-accent text-white border-accent"
          : "bg-bg-input border-border text-text-secondary hover:text-text-primary",
      )}
    >
      {label}
    </button>
  );
  return (
    <div className={cn("flex flex-wrap items-center gap-1", className)}>
      <span className="text-2xs text-text-tertiary mr-0.5">Händler:</span>
      {chip("", "Alle")}
      {list.map((s) => chip(s.id, s.name))}
    </div>
  );
}

type PriceLike = {
  listPriceCents: number | null;
  netPriceCents: number | null;
  supplier: { id: string; name: string } | null;
};

/** Händler eines Artikels: ein Händler = nur Name; mehrere = Name + Einkaufspreis je Händler. Günstigster ist grün, der gefilterte steht zuerst. */
export function SupplierTags({
  prices,
  supplierId,
  stacked = false,
  wide = false,
}: {
  prices: PriceLike[];
  supplierId?: string;
  /** untereinander, rechtsbündig — für die Spalte rechts neben dem Preis */
  stacked?: boolean;
  /** Spalte breiter, weil in der Liste Artikel mit mehreren Händlern (Name + Preis) vorkommen */
  wide?: boolean;
}) {
  // Mit Händlerfilter nur der gewählte Händler (sein Preis steht schon als EK in der Zeile)
  const rows = prices.filter(
    (p) => p.supplier && (!supplierId || p.supplier.id === supplierId),
  );
  if (!rows.length) return null;
  const nets = rows
    .map((p) => p.netPriceCents)
    .filter((n): n is number => n != null);
  const cheapest = nets.length ? Math.min(...nets) : null;
  const sorted = [...rows].sort((a, b) =>
    a.supplier!.id === supplierId ? -1 : b.supplier!.id === supplierId ? 1 : 0,
  );
  return (
    <span
      className={
        stacked
          ? `flex flex-col items-start gap-0.5 shrink-0 ${wide ? "w-[128px]" : "w-[52px]"}`
          : "flex flex-wrap gap-x-2 gap-y-0.5 mt-0.5"
      }
    >
      {sorted.map((p) => (
        <span
          key={p.supplier!.id}
          // Bei nur einem Händler steht sein Preis schon als EK in der Zeile — dann nur der Name
          title={
            p.netPriceCents != null
              ? `${p.supplier!.name}: ${formatMoney(p.netPriceCents)} Einkauf`
              : p.supplier!.name
          }
          className={cn(
            "text-2xs",
            p.netPriceCents != null &&
              p.netPriceCents === cheapest &&
              rows.length > 1
              ? "text-success font-medium"
              : "text-text-tertiary",
          )}
        >
          {p.supplier!.name}
          {/* Bei mehreren Händlern steht zu jedem sein Einkaufspreis dabei (bei nur einem Händler wäre es der EK der Zeile) */}
          {rows.length > 1 && p.netPriceCents != null
            ? ` ${formatMoney(p.netPriceCents)}`
            : ""}
        </span>
      ))}
    </span>
  );
}

/**
 * Listen- und Einkaufspreis für die Anzeige: ohne Händlerfilter der niedrigste Listenpreis bzw. beste Einkaufspreis aller Händler,
 * MIT Händlerfilter die Preise genau dieses Händlers (dann steht auch nur sein Name dabei).
 */
export function shownPrices(
  prices: PriceLike[],
  supplierId: string | undefined,
  bestPurchaseCents: number | null | undefined,
): { list: number | null; purchase: number | null } {
  if (supplierId) {
    const p = prices.find((x) => x.supplier?.id === supplierId);
    if (p) return { list: p.listPriceCents, purchase: p.netPriceCents };
  }
  const lists = prices
    .map((x) => x.listPriceCents)
    .filter((n): n is number => n != null);
  return {
    list: lists.length ? Math.min(...lists) : null,
    purchase: bestPurchaseCents ?? null,
  };
}
